import { useEffect } from "react";

import { lockBodyScroll, unlockBodyScroll } from "@/utils/body-scroll-lock";
import { subscribeToMediaQuery } from "@/utils/match-media";

/**
 * React-safe wrapper around `lockBodyScroll` / `unlockBodyScroll`.
 *
 * This ensures every lock is paired with an unlock via effect cleanup, which helps prevent
 * "stuck no scroll" issues when multiple overlays/modals interact.
 */
export function useBodyScrollLock({
  key,
  enabled,
  minWidthPx,
  maxWidthPx,
}: {
  key: string;
  enabled: boolean;
  minWidthPx?: number;
  maxWidthPx?: number;
}): void {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const minMql =
      typeof minWidthPx === "number" ? window.matchMedia(`(min-width: ${minWidthPx}px)`) : null;
    const maxMql =
      typeof maxWidthPx === "number" ? window.matchMedia(`(max-width: ${maxWidthPx}px)`) : null;

    let isLocked = false;

    const sync = () => {
      const matchesMin = minMql ? minMql.matches : true;
      const matchesMax = maxMql ? maxMql.matches : true;
      const shouldLock = enabled && matchesMin && matchesMax;

      if (shouldLock && !isLocked) {
        lockBodyScroll(key);
        isLocked = true;
      } else if (!shouldLock && isLocked) {
        unlockBodyScroll(key);
        isLocked = false;
      }
    };

    sync();

    const onChange = sync;
    const unsubMin = minMql ? subscribeToMediaQuery(minMql, onChange) : null;
    const unsubMax = maxMql ? subscribeToMediaQuery(maxMql, onChange) : null;

    return () => {
      unsubMin?.();
      unsubMax?.();
      if (isLocked) unlockBodyScroll(key);
    };
  }, [enabled, key, minWidthPx, maxWidthPx]);
}
